import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import guestRequestApi from '../services/guestRequestApi';
import taskApi from '../services/taskApi';
import GuestRequestModal from './GuestRequestModal';
import EditTaskModal from './EditTaskModal';
import { formatDate, formatRelativeTime } from '../utils/dateFormatter';
import {
  GR_TYPE_LABELS as TYPE_LABELS,
  GR_STATUS_LABELS as STATUS_LABELS,
  GR_PRIORITY_LABELS as PRIORITY_LABELS
} from '../config/guestRequestLabels';
import '../styles/BookingGuestRequestsSection.css';

// Guest Requests Hub V1 — section bound to a single booking.
// Loads via guestRequestApi.list({ bookingId }), supports inline status
// updates (manual attribution) when no Task is linked, and surfaces a
// banner pointing to OTA notes when booking.specialRequests is non-empty.
//
// Labels imported from config/guestRequestLabels (V1.1 centralization).
// Local STATUS_ORDER/PRIORITY_ORDER/STATUS_OPTIONS stay here — they're
// component-specific (GR-only ordering for sort + status dropdown options).

const STATUS_ORDER = { 'new': 0, 'in-progress': 1, 'completed': 2, 'cancelled': 3 };
const PRIORITY_ORDER = { urgent: 0, normal: 1 };

const STATUS_OPTIONS = ['new', 'in-progress', 'completed', 'cancelled'];

const BookingGuestRequestsSection = ({ booking }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [rowError, setRowError] = useState(null);

  // Item C — in-place Task edit on the booking page (mirrors Task #4 on
  // /guest-requests). Clicking the linked-task badge opens EditTaskModal
  // here instead of forcing ops to navigate to /tasks.
  const [editingTask, setEditingTask] = useState(null);
  const [loadingTaskBadgeId, setLoadingTaskBadgeId] = useState(null);
  const [taskLoadError, setTaskLoadError] = useState(null);

  const bookingId = booking?._id;
  const isCancelled = booking?.status === 'cancelled';
  const hasSpecialRequests = booking?.specialRequests && booking.specialRequests.trim().length > 0;

  const fetchRequests = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await guestRequestApi.list({ bookingId });
      if (result?.success) {
        setRequests(result.requests || []);
      } else {
        setError(result?.error || 'حدث خطأ في جلب الطلبات');
      }
    } catch (err) {
      const backendMsg = err.response?.data?.error || err.response?.data?.message;
      setError(backendMsg || 'حدث خطأ في جلب الطلبات');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const sortedRequests = useMemo(() => {
    return [...requests].sort((a, b) => {
      const sa = STATUS_ORDER[a.status] ?? 99;
      const sb = STATUS_ORDER[b.status] ?? 99;
      if (sa !== sb) return sa - sb;
      const pa = PRIORITY_ORDER[a.priority] ?? 99;
      const pb = PRIORITY_ORDER[b.priority] ?? 99;
      if (pa !== pb) return pa - pb;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [requests]);

  const handleBannerClick = () => {
    const target = document.getElementById('booking-ota-notes');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCreated = (newRequest) => {
    setRequests((prev) => [newRequest, ...prev]);
    setShowModal(false);
  };

  // Item C — fetch the full Task (EditTaskModal expects a complete object,
  // not just an ID) and open the modal in place. Backend response shape:
  // { success, data: { task } } per be/routes/taskRoutes.js.
  const handleLinkedTaskClick = async (request) => {
    const taskId = request.taskId?._id || request.taskId;
    if (!taskId) return;

    setLoadingTaskBadgeId(request._id);
    setTaskLoadError(null);
    try {
      const resp = await taskApi.getTask(taskId);
      const task = resp?.data?.task;
      if (task && task._id) {
        setEditingTask(task);
      } else {
        setTaskLoadError('تعذّر تحميل المهمة.');
      }
    } catch (err) {
      console.error('Failed to load Task for edit:', err);
      setTaskLoadError('تعذّر تحميل المهمة. حاول لاحقًا.');
    } finally {
      setLoadingTaskBadgeId(null);
    }
  };

  // Task save can flip Task.status which cascades to GR.status via the
  // existing Task→GR sync (completion path). Refetch keeps both layers
  // coherent without client-side reconciliation logic.
  const handleTaskUpdated = useCallback(() => {
    setEditingTask(null);
    fetchRequests();
  }, [fetchRequests]);

  // Optimistic status update with rollback on failure.
  // Server returns the populated/decorated record on success — we replace
  // the optimistic row with the authoritative version so any server-side
  // normalization (e.g., audit timestamps) reflects in the UI.
  const handleStatusChange = async (requestId, newStatus) => {
    const original = requests.find((r) => r._id === requestId);
    if (!original || original.status === newStatus) return;

    setUpdatingId(requestId);
    setRowError(null);
    setRequests((prev) => prev.map((r) => (r._id === requestId ? { ...r, status: newStatus } : r)));

    try {
      const result = await guestRequestApi.updateStatus(requestId, { status: newStatus });
      if (result?.success && result.request) {
        setRequests((prev) => prev.map((r) => (r._id === requestId ? result.request : r)));
      } else {
        throw new Error(result?.error || 'فشل تحديث الحالة');
      }
    } catch (err) {
      // Rollback
      setRequests((prev) => prev.map((r) => (r._id === requestId ? original : r)));
      const backendMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      setRowError(backendMsg || 'حدث خطأ في تحديث حالة الطلب');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="details-card guest-requests-section" dir="rtl">
      <div className="grs-header">
        <h2 className="grs-title">
          <i className="bi bi-clipboard-check grs-title-icon"></i>
          طلبات الضيف
          {!loading && requests.length > 0 && (
            <span className="grs-count">{requests.length}</span>
          )}
        </h2>

        {!isCancelled && (
          <Button
            variant="primary"
            onClick={() => setShowModal(true)}
            className="grs-new-btn touch-btn"
          >
            <i className="bi bi-plus-circle"></i> طلب جديد
          </Button>
        )}
      </div>

      {hasSpecialRequests && (
        <button type="button" onClick={handleBannerClick} className="grs-banner">
          <i className="bi bi-chat-left-quote"></i>
          <span className="grs-banner-text">
            ملاحظة من المنصة متوفرة لهذا الحجز — اضغط للانتقال إليها
          </span>
          <i className="bi bi-arrow-up grs-banner-arrow"></i>
        </button>
      )}

      {rowError && (
        <Alert variant="danger" className="grs-row-error" onClose={() => setRowError(null)} dismissible>
          {rowError}
        </Alert>
      )}

      {taskLoadError && (
        <Alert variant="warning" className="grs-row-error" onClose={() => setTaskLoadError(null)} dismissible>
          {taskLoadError}
        </Alert>
      )}

      {loading && (
        <div className="grs-loading">
          <Spinner animation="border" size="sm" />
          <span>جاري تحميل الطلبات...</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="danger" className="grs-error">{error}</Alert>
      )}

      {!loading && !error && sortedRequests.length === 0 && (
        <div className="grs-empty">
          <i className="bi bi-inbox grs-empty-icon"></i>
          <p>لا توجد طلبات لهذا الحجز</p>
          {!isCancelled && (
            <p className="grs-empty-hint">
              اضغط على "طلب جديد" لإضافة أول طلب للضيف
            </p>
          )}
        </div>
      )}

      {!loading && !error && sortedRequests.length > 0 && (
        <div className="grs-list">
          {sortedRequests.map((req) => {
            const hasLinkedTask = !!req.linkedTaskTicketId;
            const isUpdating = updatingId === req._id;

            return (
              <div key={req._id} className={`grs-row grs-row--${req.status}`}>
                <div className="grs-row__main">
                  <div className="grs-row__head">
                    <span className={`grs-type grs-type--${req.type}`}>
                      {TYPE_LABELS[req.type] || req.type}
                    </span>
                    <span className={`grs-priority grs-priority--${req.priority}`}>
                      {PRIORITY_LABELS[req.priority] || req.priority}
                    </span>
                    {req.fulfillmentType === 'external' && (
                      <span className="grs-fulfillment grs-fulfillment--external">
                        تنفيذ خارجي
                      </span>
                    )}
                    <span className="grs-timestamp">{formatRelativeTime(req.createdAt)}</span>
                  </div>

                  {req.description && (
                    <p className="grs-description">{req.description}</p>
                  )}

                  <div className="grs-row__meta">
                    {req.requestedForDate && (
                      <span className="grs-requested-for" title="موعد التنفيذ المطلوب من الضيف">
                        <i className="bi bi-calendar-event"></i> موعد التنفيذ المطلوب: {formatDate(req.requestedForDate)}
                      </span>
                    )}
                    {req.assignedTo && (
                      <span className="grs-assigned">
                        <i className="bi bi-person"></i> {req.assignedTo}
                      </span>
                    )}
                    {hasLinkedTask && (
                      <button
                        type="button"
                        className="grs-task-link grs-task-link--clickable"
                        onClick={() => handleLinkedTaskClick(req)}
                        disabled={loadingTaskBadgeId === req._id}
                        title="فتح المهمة المرتبطة"
                      >
                        {loadingTaskBadgeId === req._id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="bi bi-link-45deg"></i>
                        )}
                        مهمة #{req.linkedTaskTicketId}
                        {req.linkedTaskStatus && (
                          <span className={`grs-task-status grs-task-status--${req.linkedTaskStatus}`}>
                            {STATUS_LABELS[req.linkedTaskStatus] || req.linkedTaskStatus}
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* GR status hidden when a Task is linked — Task status (in
                 * the meta line above) is the single source of truth.
                 * Avoids the dual-status confusion users observed when GR
                 * status (e.g. 'new') diverges from Task status (e.g.
                 * 'assigned'). Backend Task→GR sync still keeps GR.status
                 * coherent for audit; we just don't render it here. */}
                {!hasLinkedTask && (
                  <div className="grs-row__status">
                    <Form.Select
                      size="sm"
                      value={req.status}
                      onChange={(e) => handleStatusChange(req._id, e.target.value)}
                      disabled={isUpdating}
                      className={`grs-status-select grs-status-select--${req.status}`}
                      dir="rtl"
                      aria-label={`تحديث حالة الطلب`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      ))}
                    </Form.Select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <GuestRequestModal
        show={showModal}
        onHide={() => setShowModal(false)}
        booking={booking}
        onCreated={handleCreated}
      />

      <EditTaskModal
        show={!!editingTask}
        task={editingTask}
        onHide={() => setEditingTask(null)}
        onTaskUpdated={handleTaskUpdated}
      />
    </div>
  );
};

export default BookingGuestRequestsSection;
